// src/lib/api.ts
//
// Live-data access layer: maps the FastAPI rows (Postgres, FileMaker
// import) onto the frontend's Product shape from demoData.ts.
// The public catalog + product detail read through this; demoData
// remains for partner shops, orders and other not-yet-migrated areas.

import type { Category, Product, VariantTableData } from "@/lib/demoData";
import { getKeycloak } from "@/lib/keycloak";
import { BFF_ENABLED, bffCsrfHeaders } from "@/lib/bffAuth";
import i18n from "@/i18n";

/** Active UI language narrowed to the translated locales; English is
 * the fallback (attribute labels always ship all three). */
function currentLocale(): "fr" | "nl" | "en" {
  const lang = (i18n.language || "").toLowerCase().split(/[-_]/)[0];
  return lang === "fr" || lang === "nl" ? lang : "en";
}

const SPEC_BOOL_LABELS: Record<"en" | "fr" | "nl", [string, string]> = {
  en: ["Yes", "No"],
  fr: ["Oui", "Non"],
  nl: ["Ja", "Nee"],
};

/** Humanize a snake_case specs-object key: "net_weight_kg" -> "Net weight (kg)". */
function humanizeSpecKey(key: string): string {
  const unitMatch = key.match(/_(mm|cm|kg|in)$/);
  const matchedUnit = unitMatch?.[1];
  const unit = matchedUnit ? ` (${matchedUnit})` : "";
  const base = matchedUnit ? key.slice(0, -(matchedUnit.length + 1)) : key;
  const words = base.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1) + unit;
}

/** Flatten the taxonomy enrichment (migration 024 detail payload) into
 * the product page's key/value Specifications list: localized attribute
 * values in definition order, structured dims, then the free-form specs
 * object (family, weights, packaging, provenance). */
export function mapTaxonomySpecs(
  r: ApiProduct,
): Array<{ key: string; value: string }> {
  const out: Array<{ key: string; value: string }> = [];
  const locale = currentLocale();
  for (const a of r.attribute_values ?? []) {
    if (a.value == null) continue;
    const label =
      locale === "fr" ? a.label_fr : locale === "nl" ? a.label_nl : a.label_en;
    let value: string;
    if (a.type === "bool") {
      value = SPEC_BOOL_LABELS[locale][a.value ? 0 : 1];
    } else if (a.type === "number") {
      value = `${a.value}${a.unit ? ` ${a.unit}` : ""}`;
    } else {
      value = String(a.value);
    }
    out.push({ key: label, value });
  }
  const dims = r.dims_mm;
  if (dims && (dims.w != null || dims.h != null || dims.d != null)) {
    const parts = [dims.w, dims.h, dims.d]
      .map((v) => (v == null ? "?" : String(v)))
      .slice(0, dims.d != null ? 3 : 2);
    out.push({ key: "Dimensions", value: `${parts.join(" × ")} mm` });
  }
  for (const [key, value] of Object.entries(r.specs ?? {})) {
    if (value == null) continue;
    out.push({
      key: humanizeSpecKey(key),
      value: typeof value === "object" ? JSON.stringify(value) : String(value),
    });
  }
  return out;
}

/** Headers for staff-gated API endpoints. In BFF mode (default) the
 * browser authenticates with the HttpOnly session cookie and this
 * returns the CSRF header; bearer mode (VITE_BFF_AUTH=0, local
 * debugging only) returns the Authorization header instead.
 * Empty object when signed out — the API fails closed with 401. */
export function authHeaders(): Record<string, string> {
  if (BFF_ENABLED) return bffCsrfHeaders();
  const kc = getKeycloak();
  return kc?.token ? { Authorization: `Bearer ${kc.token}` } : {};
}

export interface ApiProduct {
  product_id: number;
  ean: string | null;
  brand_raw: string | null;
  model: string | null;
  name: string | null;
  full_name: string | null;
  /** Canonical vendor display name from the taxonomy enrichment
   * (Shop-owned; FileMaker's full_name stays the master-data name). */
  display_name?: string | null;
  category: string | null; // raw FileMaker category (87% NULL)
  derived_category: Category;
  /** Real taxonomy label from the subcategory (migration 024). */
  taxonomy_label?: { slug: string; en: string | null; fr: string | null;
                     nl: string | null } | null;
  unit: string | null;
  active: string | null;
  date_added: string | null;
  url: string | null;
  has_image?: boolean;
  stock_status?: string;
  image_path: string | null; // present in non-public endpoints (e.g. cart) but never from /api/products
  weight_kg: number | null;
  cost_price: number | null;
  sale_price_htva: number | null;
  sale_price_tvac: number | null;
  /** Taxonomy enrichment (migration 024), detail endpoint only. */
  dims_mm?: { w?: number; h?: number; d?: number } | null;
  specs?: Record<string, unknown> | null;
  subcategory_slug?: string | null;
  attribute_values?: ApiAttributeValue[];
  variant_table?: VariantTableData | null;
}

export interface ApiAttributeValue {
  key: string;
  label_fr: string;
  label_nl: string;
  label_en: string;
  type: "enum" | "number" | "bool" | "text";
  unit: string | null;
  value: string | number | boolean | null;
}

export interface ProductPage {
  total: number;
  rows: ApiProduct[];
}

export interface BrandRow {
  brand_id: number | null;
  name: string;
  count: number;
  logo_url?: string | null;
  /** True when a real manually managed logo asset is stored for the
   * brand; false/undefined means logo_url is the placeholder SVG. */
  has_logo?: boolean;
}

export interface ProductQuery {
  q?: string | undefined;
  category?: string | undefined;
  /** Taxonomy slug (migration 024), any level — descendants included. */
  cat?: string | undefined;
  brand?: string | undefined;
  sort?: "name" | "price_asc" | "price_desc" | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/** Public taxonomy tree entry (GET /api/categories). */
export interface TaxCategory {
  id: number;
  slug: string;
  parent_id: number | null;
  kind: string;
  sort_order: number;
  names: Record<string, string>;
  product_count: number;
}

export function fetchCategories(): Promise<{ categories: TaxCategory[] }> {
  return getJson<{ categories: TaxCategory[] }>("/api/categories");
}

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(path);
  if (!r.ok) throw new Error(`API ${path}: HTTP ${r.status}`);
  return (await r.json()) as T;
}

export function fetchProducts(q: ProductQuery): Promise<ProductPage> {
  const p = new URLSearchParams();
  if (q.q) p.set("q", q.q);
  if (q.category) p.set("category", q.category);
  if (q.cat) p.set("cat", q.cat);
  if (q.brand) p.set("brand", q.brand);
  if (q.sort) p.set("sort", q.sort);
  p.set("limit", String(q.limit ?? 60));
  p.set("offset", String(q.offset ?? 0));
  return getJson<ProductPage>(`/api/products?${p}`);
}

export function fetchProduct(productId: string): Promise<ApiProduct> {
  return getJson<ApiProduct>(`/api/products/${encodeURIComponent(productId)}`);
}

export function fetchBrands(): Promise<{ rows: BrandRow[] }> {
  return getJson<{ rows: BrandRow[] }>("/api/brands");
}

// ---------- catalog visibility (admin-only) ----------

/** Visibility filter for the catalog. Admins can scope the catalog
 * to only show hidden rows; customers always see visible only. */
export type VisibilityFilter = "visible" | "hidden" | "all";

/** Per-entity hide flags returned by /api/admin/visibility/stats. */
export interface VisibilityStats {
  products: { total: number; hidden: number };
  brands: { total: number; hidden: number };
  customers: { total: number; hidden: number };
}

/** Filter object mirroring /api/products search params. All fields
 * accept string OR undefined so the bulk-hide call site can spread
 * possibly-empty URL params without narrowing. */
export interface CatalogFilter {
  q?: string | undefined;
  category?: string | undefined;
  brand?: string | undefined;
}

/** Hide or unhide a list of entities (scope=selected) or every
 * entity matching a filter (scope=filter). Returns the count of
 * rows affected on success, or throws on a non-2xx response. */
async function bulkVisibility(
  action: "hide" | "unhide",
  body: {
    entity_type: "product" | "brand" | "customer";
    scope: "selected" | "filter";
    ids?: number[];
    filter?: CatalogFilter;
  },
): Promise<{ affected: number }> {
  const r = await fetch(`/api/admin/visibility/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw new Error(
      `API /api/admin/visibility/${action}: HTTP ${r.status} ${await r.text()}`,
    );
  }
  return (await r.json()) as { affected: number };
}

export function hideEntities(body: Parameters<typeof bulkVisibility>[1]) {
  return bulkVisibility("hide", body);
}

export function unhideEntities(body: Parameters<typeof bulkVisibility>[1]) {
  return bulkVisibility("unhide", body);
}

export function fetchVisibilityStats(): Promise<VisibilityStats> {
  return getJson<VisibilityStats>("/api/admin/visibility/stats");
}

// ---------- admin taxonomy (category assignment) ----------

export interface AdminTaxRow {
  product_id: number;
  full_name: string;
  brand_raw: string | null;
  subcategory_id: number | null;
  subcategory_slug: string | null;
  suggested_slug: string | null;
}

export async function adminTaxonomyProducts(q: {
  q?: string | undefined;
  brand?: string | undefined;
  mapping?: "all" | "unmapped" | "mapped";
  limit?: number;
  offset?: number;
}): Promise<{ total: number; rows: AdminTaxRow[] }> {
  const p = new URLSearchParams();
  if (q.q) p.set("q", q.q);
  if (q.brand) p.set("brand", q.brand);
  p.set("mapping", q.mapping ?? "all");
  p.set("limit", String(q.limit ?? 60));
  p.set("offset", String(q.offset ?? 0));
  const r = await fetch(`/api/admin/taxonomy/products?${p}`, {
    headers: { ...authHeaders() },
  });
  if (!r.ok) throw new Error(`taxonomy products: HTTP ${r.status}`);
  return (await r.json()) as { total: number; rows: AdminTaxRow[] };
}

async function taxWrite<T>(path: string, method: string,
                           body: unknown): Promise<T> {
  const r = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${path}: HTTP ${r.status} ${await r.text()}`);
  return (await r.json()) as T;
}

export function assignProductCategory(productId: number,
                                      subcategoryId: number | null) {
  return taxWrite(`/api/admin/taxonomy/products/${productId}`, "PUT",
                  { subcategory_id: subcategoryId });
}

export function assignBrandCategory(brandId: number,
                                    subcategoryId: number | null,
                                    onlyUnmapped = true) {
  return taxWrite<{ affected: number }>(
    `/api/admin/taxonomy/brands/${brandId}`, "PUT",
    { subcategory_id: subcategoryId, only_unmapped: onlyUnmapped });
}

export function acceptTaxonomySuggestions(brand?: string) {
  return taxWrite<{ mapped: number; skipped: number }>(
    "/api/admin/taxonomy/accept-suggestions", "POST",
    { brand: brand || null });
}

export function setProductVisibility(
  productId: number,
  hide: boolean,
): Promise<{ affected: number }> {
  return hide
    ? hideEntities({
        entity_type: "product",
        scope: "selected",
        ids: [productId],
      })
    : unhideEntities({
        entity_type: "product",
        scope: "selected",
        ids: [productId],
      });
}

export function setBrandVisibility(
  brandId: number,
  hide: boolean,
): Promise<{ affected: number }> {
  return hide
    ? hideEntities({
        entity_type: "brand",
        scope: "selected",
        ids: [brandId],
      })
    : unhideEntities({
        entity_type: "brand",
        scope: "selected",
        ids: [brandId],
      });
}

export function setCustomerVisibility(
  customerId: number,
  hide: boolean,
): Promise<{ affected: number }> {
  return hide
    ? hideEntities({
        entity_type: "customer",
        scope: "selected",
        ids: [customerId],
      })
    : unhideEntities({
        entity_type: "customer",
        scope: "selected",
        ids: [customerId],
      });
}

// ---------- brand logos (admin-only) ----------

/** Extract the FastAPI `detail` message from an error response so the
 * admin UI can surface the backend's own wording. Falls back to a
 * generic HTTP status error when the body has no detail string. */
async function apiError(r: Response, path: string): Promise<Error> {
  let detail = "";
  try {
    const j = (await r.json()) as { detail?: unknown };
    if (typeof j?.detail === "string") detail = j.detail;
  } catch {
    // non-JSON error body — fall through to the generic message
  }
  return new Error(detail || `API ${path}: HTTP ${r.status}`);
}

export interface BrandLogoUploadResult {
  brand_id: number;
  slug: string;
  logo_url: string;
}

/** Upload/replace a brand's logo (admin). Multipart form field `file`;
 * Content-Type is intentionally NOT set — the browser adds the
 * multipart boundary. */
export async function uploadBrandLogo(
  brandId: number,
  file: File,
): Promise<BrandLogoUploadResult> {
  const path = `/api/admin/brands/${brandId}/logo`;
  const form = new FormData();
  form.append("file", file);
  const r = await fetch(path, {
    method: "PUT",
    headers: authHeaders(),
    body: form,
  });
  if (!r.ok) throw await apiError(r, path);
  return (await r.json()) as BrandLogoUploadResult;
}

/** Remove a brand's manually managed logo (admin); the brand falls
 * back to the placeholder. */
export async function deleteBrandLogo(
  brandId: number,
): Promise<{ removed: number }> {
  const path = `/api/admin/brands/${brandId}/logo`;
  const r = await fetch(path, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!r.ok) throw await apiError(r, path);
  return (await r.json()) as { removed: number };
}

/** Map an API row onto the frontend Product shape. Real FileMaker
 * products carry no images/specs/stock — those fields get honest
 * defaults (the UI has image fallbacks). */
export function mapApiProduct(r: ApiProduct): Product {
  const specs: Array<{ key: string; value: string }> = [];
  if (r.ean) specs.push({ key: "EAN", value: r.ean });
  if (r.model) specs.push({ key: "Model", value: r.model });
  if (r.weight_kg != null) specs.push({ key: "Weight", value: `${r.weight_kg} kg` });
  if (r.unit) specs.push({ key: "Unit", value: r.unit });
  if (r.category) specs.push({ key: "Vendor category", value: r.category });
  specs.push(...mapTaxonomySpecs(r));
  return {
    sku: String(r.product_id),
    name: r.full_name ?? r.name ?? `#${r.product_id}`,
    displayName: r.display_name ?? null,
    brand: r.brand_raw ?? "",
    category: r.derived_category,
    taxonomyLabel: r.taxonomy_label
      ? { slug: r.taxonomy_label.slug, en: r.taxonomy_label.en ?? "",
          fr: r.taxonomy_label.fr ?? "", nl: r.taxonomy_label.nl ?? "" }
      : null,
    description: "",
    priceEUR: r.sale_price_tvac ?? 0,
    currency: "EUR",
    image: r.has_image ? `/api/products/${r.product_id}/image` : "",
    images: r.has_image ? [`/api/products/${r.product_id}/image`] : [],
    specs,
    tags: [r.brand_raw, r.category].filter((x): x is string => Boolean(x)),
    // Private quantities (including legacy in_stock) are not coverage proof.
    // UNKNOWN does not prohibit an order request or reserve physical stock.
    inStock: null,
    stockStatus: "UNKNOWN",
    featured: false,
    updatedAt: r.date_added ?? "",
    approved: "approved",
    vendorNotes: r.url ?? undefined,
    variantTable: r.variant_table ?? null,
  };
}
